import * as Sentry from "@sentry/react";
import { TRANSPORT_MESSAGE } from "@/lib/transportFailure";

/**
 * Transport-failure presentation for the email/password signup and login forms.
 *
 * Aug 17 2026, 2:53 PM ET: a real user's connection dropped between the OPTIONS
 * preflight to /auth/v1/signup (200, logged at the gateway) and the POST, which
 * never arrived. SignUp.tsx rendered `error.message` verbatim, so what she read
 * was "Load failed (unnzlorzyswiahjlxwqh.supabase.co)" — our Supabase hostname,
 * and a sentence that tells a mom nothing about what to do next. She DM'd us on
 * Instagram because there was no other signal in the UI.
 *
 * Nothing here fixes the network. This decides only what she reads when it
 * fails, and keeps the real error going to Sentry.
 */
export const NETWORK_ERROR_MESSAGE =
  "Couldn't reach Kin. Check your connection and try again.";

// The regex moved to `@/lib/transportFailure` in #1709 so the title paths
// could reuse the exact same wording without a second copy. Imported rather
// than redefined: two copies of this list drifting apart is the failure mode.
// Nothing about `isAuthNetworkError`'s behaviour changed.

/**
 * True only when the request never got an answer from Supabase.
 *
 * If Supabase responded at all — wrong password, email already registered,
 * invalid email, rate limit — this is false and the caller's existing copy
 * stands. Those messages are already right and must not be reworded.
 *
 * On the shape check: supabase-js does NOT let the raw TypeError out. auth-js
 * catches it in _handleRequest and rethrows `new AuthRetryableFetchError(msg, 0)`,
 * which signUp/signInWithPassword then return on the `{ error }` field — so in
 * production `error.name` is "AuthRetryableFetchError", never "TypeError", and
 * a name === "TypeError" check alone would be dead code. The `status` field is
 * what separates the two causes of that one class:
 *
 *   status === 0    fetch itself threw — no response, ever (this is us)
 *   status >= 500   Supabase answered with a server error (not us; it responded)
 *
 * status === 0 also covers AbortError/timeouts, whose wrapped message wording is
 * browser-specific and not worth matching on. The raw-shape checks below are
 * kept for any caller that sees an unwrapped throw.
 */
export function isAuthNetworkError(error: unknown): boolean {
  if (typeof error !== "object" || error === null) return false;
  const { name, message, status } = error as {
    name?: unknown;
    message?: unknown;
    status?: unknown;
  };

  if (name === "AuthRetryableFetchError") return status === 0;
  if (name === "AbortError") return true;

  return (
    name === "TypeError" &&
    typeof message === "string" &&
    TRANSPORT_MESSAGE.test(message)
  );
}

/**
 * The user reads NETWORK_ERROR_MESSAGE; Sentry gets the untouched original, so
 * we keep the hostname, the wording and the stack. Tagged so these are
 * filterable away from real auth failures.
 */
export function reportAuthNetworkError(
  error: unknown,
  flow: "signup" | "login",
): void {
  const err =
    error instanceof Error
      ? error
      : new Error(String((error as { message?: unknown } | null)?.message ?? error));
  Sentry.captureException(err, {
    tags: { auth_flow: flow, error_class: "network" },
  });
}
